import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiShield, FiWifi, FiCar, FiCoffee } = FiIcons;

function About() {
  const features = [
    {
      icon: FiMapPin,
      title: 'Strategic Location',
      description: 'Located in Ullinduwawa town center, we serve as the perfect basecamp for Morning Side adventures.',
    },
    {
      icon: FiShield,
      title: 'Reliable & Safe',
      description: 'Secure parking, 24/7 front desk, and the last reliable stop before your rainforest expedition.',
    },
    {
      icon: FiWifi,
      title: 'Stay Connected',
      description: 'High-speed internet (20Mbps) perfect for digital nomads and sharing your adventures.',
    },
    {
      icon: FiCoffee,
      title: 'Authentic Cuisine',
      description: 'Traditional Sri Lankan dishes with Halal, Vegan, and Vegetarian options available.',
    },
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-4xl md:text-5xl font-serif font-bold text-white mb-6"
              >
                The Gateway to
                <span className="gradient-text block">Morning Side</span>
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-lg text-gray-300 leading-relaxed mb-6"
              >
                Unlike remote jungle lodges, Yaman offers the perfect blend of accessibility and adventure. 
                We're strategically positioned in Kolonna town, providing you with civilization's amenities 
                on the edge of the wild.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="text-gray-300 leading-relaxed"
              >
                As the closest town accommodation to the Morning Side entrance of Sinharaja Forest Reserve, 
                we serve as your reliable transfer point where the sedan is parked and the 4x4 adventure begins.
              </motion.p>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="glass-morphism rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-emerald-400 mb-2">15km</div>
                <div className="text-sm text-gray-300">To Morning Side</div>
              </div>
              <div className="glass-morphism rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-emerald-400 mb-2">4.8★</div>
                <div className="text-sm text-gray-300">Guest Rating</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid sm:grid-cols-2 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                className="glass-morphism rounded-xl p-6 hover:glass-morphism-dark transition-all duration-300"
              >
                <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-16 glass-morphism rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-serif font-bold text-white mb-4">
            Why Choose Yaman as Your Basecamp?
          </h3>
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            <div className="space-y-3">
              <SafeIcon icon={FiClock} className="w-8 h-8 text-emerald-400 mx-auto" />
              <h4 className="font-semibold text-white">Save Time</h4>
              <p className="text-sm text-gray-300">
                Every minute saved is an extra minute of sleep before your 5 AM trek
              </p>
            </div>
            <div className="space-y-3">
              <SafeIcon icon={FiCar} className="w-8 h-8 text-emerald-400 mx-auto" />
              <h4 className="font-semibold text-white">Secure Parking</h4>
              <p className="text-sm text-gray-300">
                Safe, enclosed parking for rental vehicles in a rural town setting
              </p>
            </div>
            <div className="space-y-3">
              <SafeIcon icon={FiShield} className="w-8 h-8 text-emerald-400 mx-auto" />
              <h4 className="font-semibold text-white">Reliability</h4>
              <p className="text-sm text-gray-300">
                Hot meals, clean rooms, and connectivity when you need them most
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default About;